/**
 * Moldura da área logada: topo com marca, menu, IA, sol/lua e conta;
 * no celular, barra inferior com 4 atalhos + "Mais".
 */
import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Avatar, Icon, SheetHead, TextField, useDialog, useToast } from '@dotapps/ui';
import { api } from '../lib/api';
import { requestCalendarCode } from '../lib/google';
import { keys, useBudgets, useContracts, useMeetings, useProjects, useReceivables, useSettings } from '../lib/queries';
import { needsAttention } from '../features/reports/Receivables';
import { useBriefings } from '../features/briefing/briefingApi';
import { useQueryClient } from '@tanstack/react-query';
import { ROLE_LABEL, type User } from '@dotapps/core';
import { AssistantDrawer } from '../features/assistant/AssistantDrawer';
import type { TenantExtension } from '../tenants/registry';
import { useAuth } from './auth';
import { ViewAsBanner } from './CarteiraBar';
import { NAV, allowed, isActive, navLabel, tabPaths, type NavItem } from './nav';
import { useTenant } from './tenant';
import { useToday } from '../lib/hooks';

function useBadges() {
  const today = useToday();
  const meetings = useMeetings().data ?? [];
  const budgets = useBudgets().data ?? [];
  const contracts = useContracts().data ?? [];
  const projects = useProjects().data ?? [];
  const { isAdmin, role } = useAuth();
  const receivables = useReceivables(isAdmin).data ?? [];
  const briefings = useBriefings().data ?? [];
  return (path: string): number => {
    // projetista: briefings novos no Painel; administrador: versões aguardando aprovação
    if (path === '/' && role === 'projetista') return briefings.filter(b => b.status === 'recebido').length;
    if (path === '/briefing') return isAdmin ? briefings.filter(b => b.status === 'revisao').length : 0;
    if (path === '/relatorios') return receivables.filter(r => needsAttention(r, today)).length;
    if (path === '/agenda') return meetings.filter(m => m.date === today).length;
    if (path === '/orcamentos') return budgets.filter(b => b.status === 'aguardando').length;
    if (path === '/contratos') return contracts.filter(c => c.status === 'assinatura').length;
    if (path === '/obras')
      return projects.reduce(
        (a, p) => a + p.tasks.filter(t => t.status === 'parada' || t.status === 'atrasada').length,
        0,
      );
    return 0;
  };
}

function useNavItems(ext: TenantExtension): NavItem[] {
  const features = useSettings().data?.features;
  const { role } = useAuth();
  const base = NAV.filter(n => (!n.feature || features?.[n.feature] !== false) && allowed(n, role));
  return [...base, ...(ext.navItems ?? []).filter(n => allowed(n, role))];
}

export function AppShell({ extension }: { extension: TenantExtension }) {
  const { tenant, logo, mode, toggleMode } = useTenant();
  const { user, role, viewAs } = useAuth();
  const dialog = useDialog();
  const { pathname } = useLocation();
  const badge = useBadges();
  const items = useNavItems(extension);
  const TAB_PATHS = tabPaths(role);
  // tela fora do perfil (ex.: link antigo): volta ao Painel
  const blocked = NAV.some(n => isActive(n.path, pathname) && n.path !== '/' && !allowed(n, role));

  useEffect(() => {
    // chaves obrigatórias: o Chrome novo faz scrollTo devolver uma Promise, e o React trataria isso como função de limpeza
    window.scrollTo({ top: 0 });
  }, [pathname]);

  const current = items.find(i => isActive(i.path, pathname));
  const inTab = TAB_PATHS.some(p => isActive(p, pathname));
  const moreBadge = items.filter(i => !TAB_PATHS.includes(i.path)).reduce((a, i) => a + badge(i.path), 0);

  return (
    <>
      <header className="topbar">
        <div className="bar">
          <NavLink to="/" className="brand" aria-label={tenant.name}>
            {logo ? (
              <img className="brand-logo" src={logo} alt={tenant.name} crossOrigin="anonymous" />
            ) : (
              <>
                <span className="mk">{tenant.name.split(' ')[0].slice(0, 4).toUpperCase()}</span>
                <span className="brand-text">
                  <b>{tenant.name}</b>
                  <small>{tenant.tagline}</small>
                </span>
              </>
            )}
          </NavLink>
          <nav id="nav" aria-label="Menu principal">
            {items.map(n => {
              const b = badge(n.path);
              return (
                <NavLink
                  key={n.path}
                  to={n.path}
                  end={n.path === '/'}
                  className={() => `nav-item${isActive(n.path, pathname) ? ' on' : ''}`}
                >
                  <Icon name={n.icon} />
                  <span>{navLabel(n, role)}</span>
                  {b > 0 && <em className="nav-badge">{b}</em>}
                </NavLink>
              );
            })}
          </nav>
          <div className="bar-r">
            {tenant.features.ia !== false && (
              <button
                className="ai-btn"
                onClick={() => dialog.open(<AssistantDrawer />, { drawer: true })}
                aria-label={`Pergunte à ${tenant.assistantName}`}
              >
                <Icon name="spark" />
                <span>IA</span>
              </button>
            )}
            <button
              className="theme-toggle"
              onClick={toggleMode}
              aria-label="Alternar modo sol e lua"
              title="Modo sol / lua"
              aria-pressed={mode === 'light'}
            >
              <span className="tt-sun">
                <Icon name="sun" />
              </span>
              <span className="tt-moon">
                <Icon name="moon" />
              </span>
            </button>
            <div id="userSlot">
              {user && (
                <button className="user-chip" onClick={() => dialog.open(<AccountSheet user={user} />)}>
                  <Avatar ini={user.ini} />
                  <span className="user-meta">
                    <b>{user.name}</b>
                    <small>
                      {ROLE_LABEL[user.role]}
                      {user.cargo ? ` · ${user.cargo.split('·').pop()?.trim()}` : ''}
                    </small>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {viewAs && <ViewAsBanner />}

      <main id="view" key={pathname.split('/')[1]} className="view-in">
        {blocked ? <Navigate to="/" replace /> : <Outlet />}
      </main>

      <nav id="tabbar" aria-label="Menu do celular">
        {items
          .filter(i => TAB_PATHS.includes(i.path))
          .map(i => {
            const b = badge(i.path);
            return (
              <NavLink
                key={i.path}
                to={i.path}
                end={i.path === '/'}
                className={() => `tab-item${isActive(i.path, pathname) ? ' on' : ''}`}
              >
                <Icon name={i.icon} />
                <span>{i.path === '/' ? navLabel(i, role) : (i.short ?? i.label)}</span>
                {b > 0 && <em className="nav-badge">{b}</em>}
              </NavLink>
            );
          })}
        <button
          className={`tab-item${inTab ? '' : ' on'}`}
          onClick={() => dialog.open(<MoreMenu items={items} badge={badge} />)}
        >
          <Icon name="menu" />
          <span>{inTab ? 'Mais' : current ? navLabel(current, role) : 'Mais'}</span>
          {moreBadge > 0 && <em className="nav-badge">{moreBadge}</em>}
        </button>
      </nav>
    </>
  );
}

/** Menu "Mais" do celular. */
function MoreMenu({ items, badge }: { items: NavItem[]; badge: (p: string) => number }) {
  const { close } = useDialog();
  const dialog = useDialog();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, role, logout } = useAuth();
  const { toggleMode, tenant } = useTenant();
  return (
    <>
      <SheetHead title="Menu" sub={`${user?.name ?? ''} · ${user ? ROLE_LABEL[user.role] : ''}`} />
      <div className="sheet-body">
        <div className="more-grid">
          {items.map(n => {
            const b = badge(n.path);
            return (
              <button
                key={n.path}
                className={`more-tile${isActive(n.path, pathname) ? ' on' : ''}`}
                onClick={() => {
                  close();
                  navigate(n.path);
                }}
              >
                <Icon name={n.icon} />
                <span>{navLabel(n, role)}</span>
                {b > 0 && <em className="nav-badge">{b}</em>}
              </button>
            );
          })}
        </div>
        <div className="btn-row">
          {tenant.features.ia !== false && (
            <button className="btn ghost" onClick={() => dialog.open(<AssistantDrawer />, { drawer: true })}>
              <Icon name="spark" />
              Pergunte à {tenant.assistantName}
            </button>
          )}
          <button className="btn ghost" onClick={toggleMode}>
            <Icon name="sun" />
            Sol / lua
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              close();
              logout();
            }}
          >
            <Icon name="back" />
            Sair
          </button>
        </div>
      </div>
    </>
  );
}

/** Criar ou trocar a própria senha (opcional para quem entra pelo Google). */
function PasswordSection({ user }: { user: User }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const has = user.hasPassword;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 8) return toast('Use pelo menos 8 caracteres.', 'warn');
    if (next !== again) return toast('As duas senhas novas não são iguais.', 'warn');
    setBusy(true);
    try {
      await api.post('/auth/password', has ? { current, next } : { next });
      qc.setQueryData<User>(keys.me, u => (u ? { ...u, hasPassword: true } : u));
      toast(has ? 'Senha alterada.' : 'Senha criada. Agora você também entra com e-mail e senha.');
      setOpen(false);
      setCurrent('');
      setNext('');
      setAgain('');
    } catch (err) {
      toast((err as Error).message, 'warn');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h4 className="sub-h">Senha</h4>
      {!open ? (
        <div className="btn-row">
          <p className="muted small">
            {has
              ? 'Você entra com o Google ou com e-mail e senha.'
              : 'Você entra com o Google. Se quiser, crie uma senha para entrar também com e-mail.'}
          </p>
          <button className="btn ghost" onClick={() => setOpen(true)}>
            <Icon name="lock" />
            {has ? 'Alterar minha senha' : 'Criar senha'}
          </button>
        </div>
      ) : (
        <form className="login-form" onSubmit={save}>
          {has && (
            <TextField
              label="Senha atual"
              type="password"
              autoComplete="current-password"
              value={current}
              onValue={setCurrent}
              required
            />
          )}
          <TextField
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            hint="Pelo menos 8 caracteres"
            value={next}
            onValue={setNext}
            required
          />
          <TextField
            label="Repita a nova senha"
            type="password"
            autoComplete="new-password"
            value={again}
            onValue={setAgain}
            required
          />
          <div className="btn-row">
            <button type="button" className="btn ghost" onClick={() => setOpen(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn primary" disabled={busy}>
              <Icon name="check" />
              {busy ? 'Salvando…' : 'Salvar senha'}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

/** Conta: sair, sol/lua, senha e conexão da Google Agenda. */
export function AccountSheet({ user }: { user: User }) {
  const { close } = useDialog();
  const { logout } = useAuth();
  const { toggleMode, tenant } = useTenant();
  const toast = useToast();
  const qc = useQueryClient();
  const settings = useSettings().data;
  const [busy, setBusy] = useState(false);
  const calendarReady =
    !!settings?.integrations.calendar && !!tenant.googleClientId && tenant.features.googleCalendar !== false;

  async function connect() {
    setBusy(true);
    try {
      const code = await requestCalendarCode(tenant.googleClientId!, user.email);
      const u = await api.post<User>('/auth/calendar', { code });
      qc.setQueryData(keys.me, u);
      await qc.invalidateQueries({ queryKey: keys.users });
      // administrador: os recebimentos pendentes também entram na agenda dele
      if (u.role === 'admin') {
        await api.post('/receivables/calendar-sync').catch(() => undefined);
        await qc.invalidateQueries({ queryKey: keys.receivables });
      }
      toast(
        u.role === 'admin'
          ? 'Google Agenda conectada. Reuniões e recebimentos entram na sua agenda.'
          : 'Google Agenda conectada. Novas reuniões entram na sua agenda com convite ao cliente.',
      );
    } catch (err) {
      toast((err as Error).message, 'warn');
    } finally {
      setBusy(false);
    }
  }
  async function disconnect() {
    const u = await api.del<User>('/auth/calendar');
    qc.setQueryData(keys.me, u);
    toast('Google Agenda desconectada');
  }

  return (
    <>
      <SheetHead title="Sua conta" sub={`${user.email} · ${tenant.name}`} />
      <div className="sheet-body">
        <div className="btn-row">
          <button
            className="btn ghost"
            onClick={() => {
              close();
              logout();
            }}
          >
            <Icon name="back" />
            Sair
          </button>
          <button className="btn ghost" onClick={toggleMode}>
            <Icon name="sun" />
            Modo sol / lua
          </button>
        </div>
        <PasswordSection user={user} />
        <h4 className="sub-h">Google Agenda</h4>
        {calendarReady ? (
          user.calendarLinked ? (
            <div className="sync-ok">
              <Icon name="cal" />
              <div>
                <b>Conectada</b>
                <small>
                  Reuniões que você agenda vão para a sua Google Agenda, com link do Meet e convite por e-mail ao
                  cliente.
                </small>
                <button className="link" onClick={disconnect}>
                  Desconectar
                </button>
              </div>
            </div>
          ) : (
            <button className="btn primary" disabled={busy} onClick={connect}>
              <Icon name="cal" />
              Conectar minha Google Agenda
            </button>
          )
        ) : (
          <p className="muted small">
            A integração com a Google Agenda ainda não foi configurada pelo administrador da plataforma (veja
            docs/deploy.md).
          </p>
        )}
      </div>
    </>
  );
}
